import { useSuspenseQuery } from '@tanstack/react-query';
import { fetchRestaurants } from './api';
import { useAtom } from 'jotai';
import restaurantStore, { restaurantAtom } from '~/App/store/restaurant';
import { Suspense, useEffect, useState } from 'react';
import { Modal, Result } from 'antd';
import ErrorBoundary from '~/App/components/ErrorBoundary';
import Loading from '~/App/components/Loading';
import { RestaurantItem } from '@restaurant/types';
import storage from '~/storage';
import { RightOutlined, ShopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const useRestaurantItems = () => {
  const { data } = useSuspenseQuery({
    queryKey: ['restaurants'],
    queryFn: async () => {
      const { restaurants } = await fetchRestaurants();
      return restaurants ?? [];
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data;
};

const SelectRestaurantModal = ({
  onSelect,
}: {
  onSelect: (item: RestaurantItem) => void;
}) => {
  const items = useRestaurantItems();

  if (items.length) {
    return (
      <div className="flex flex-col gap-2">
        {items.map((item) => {
          return (
            <div
              key={item.restaurantId}
              className="rounded-lg p-4 hover:bg-neutral-100 transition-colors cursor-pointer border border-solid border-neutral-200"
              onClick={() => onSelect(item)}
            >
              <p>{item.name}</p>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center">
      <Result
        status="error"
        title="Tidak ada akses restoran"
        subTitle="Harap minta admin untuk memberikan akses restoran"
      />
    </div>
  );
};

const STORAGE_KEY = 'restaurantId';

const SelectRestaurant = () => {
  const restaurantItems = useRestaurantItems();
  const [showModal, setShowModal] = useState(false);
  const [restaurant, _setRestaurant] = useAtom(restaurantAtom);
  const saveRestaurant = (item: RestaurantItem) => {
    restaurantStore.setRestaurantId(item.restaurantId);
    storage.set(STORAGE_KEY, item.restaurantId);
    _setRestaurant(item);
  };

  const navigate = useNavigate();

  useEffect(() => {
    (() => {
      const localStorageValue = storage.get(STORAGE_KEY, null);
      if (localStorageValue != null) {
        const match = restaurantItems.find(
          ({ restaurantId }) => restaurantId === localStorageValue
        );
        if (match) {
          saveRestaurant(match);
          return;
        } else {
          storage.remove('restaurantId');
        }
      }
    })();
  }, [restaurantItems]);

  const onSelectModal = (item: RestaurantItem) => {
    saveRestaurant(item);
    setShowModal(false);
    navigate('/');
  };

  return (
    <>
      <div
        className="cursor-pointer px-4 py-3 border-neutral-300 border border-solid flex justify-between items-center gap-2 w-[240px] rounded-full"
        onClick={() => setShowModal(true)}
      >
        <ShopOutlined />
        <p className="flex-1">
          {restaurant ? (
            restaurant.name
          ) : (
            <span className="text-neutral-400">Pilih restoran</span>
          )}
        </p>
        <RightOutlined />
      </div>
      <Modal
        open={showModal}
        title="Pilih Restoran"
        onCancel={() => setShowModal(false)}
        footer={null}
        closable={true}
        maskClosable={true}
      >
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>
            <SelectRestaurantModal onSelect={onSelectModal} />
          </Suspense>
        </ErrorBoundary>
      </Modal>
    </>
  );
};

export default SelectRestaurant;
